import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Space, Spin, Tag, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { getControllerConfigurationStatusOptions } from "../../../services/client/@tanstack/react-query.gen";
import { downloadJson } from "../../../util/downloadJson";
import { extractErrorMessage } from "../../../util/extractErrorMessage";

type ControllerConfigurationCardProps = {
  site_id: string;
};

export const ControllerConfigurationCard: React.FC<
  ControllerConfigurationCardProps
> = ({ site_id }) => {
  const { data, error, isLoading, isFetching, refetch } = useQuery(
    getControllerConfigurationStatusOptions({ path: { site_id } }),
  );

  const configuration = data?.configuration;

  return (
    <Card
      size="small"
      className="mb-6"
      title="Controller configuration"
      extra={
        <Space>
          {configuration && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() =>
                downloadJson(
                  `${process.env.NODE_ENV}-controller_configuration-${site_id}.json`,
                  configuration,
                )
              }
            >
              Download
            </Button>
          )}
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={isFetching}
            onClick={() => void refetch()}
          >
            Re-check
          </Button>
        </Space>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-2">
          <Spin size="small" />
        </div>
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to check the controller configuration"
          description={extractErrorMessage(error)}
        />
      ) : data?.configured_correctly ? (
        <Space direction="vertical" size={2}>
          <Tag icon={<CheckCircleOutlined />} color="green">
            Configured correctly
          </Tag>
          <Typography.Text type="secondary">
            A controller fetching its configuration for this site is served the
            configuration below.
          </Typography.Text>
        </Space>
      ) : (
        <Space direction="vertical" size={8} className="w-full">
          <Tag icon={<CloseCircleOutlined />} color="red">
            Not configured correctly
          </Tag>
          <Alert
            type="error"
            showIcon
            message="A controller fetching its configuration for this site gets an error"
            description={
              <ul className="list-disc pl-5">
                {(data?.validation_errors ?? []).map((validationError) => (
                  <li key={validationError}>{validationError}</li>
                ))}
              </ul>
            }
          />
        </Space>
      )}
    </Card>
  );
};
